import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToolActivity } from '../../../features/pcheck/models/pcheck.model';

/** Human-readable pill labels per tool name and status. */
const LABELS_RUNNING: Record<string, string> = {
  search_sf_account:              'Searching Salesforce…',
  get_sf_contacts:                'Loading contacts…',
  map_executive:                  'Analysing executives…',
  select_executives:              'Loading recommendations…',
  init_booking_slots:             'Fetching availability…',
  confirm_booking:                'Booking meeting…',
  generate_booking_url:           'Generating booking link…',
  get_additional_guest_options:   'Loading contacts…',
  get_prep_doc:                   'Generating report…',
  generate_call_objective_options:'Generating call objectives…',
  confirm_call_objective:         'Saving call objective…',
  get_reschedule_link:            'Looking up meeting…',
  confirm_reschedule:             'Confirming reschedule…',
  get_cancel_details:             'Looking up meeting…',
  confirm_cancel:                 'Cancelling meeting…',
  get_meeting_for_add_participants:'Looking up meeting…',
  confirm_add_participants:       'Adding participants…',
  update_session_metadata:        'Updating session…',
  dispatch_to_agent:              'Routing to agent…',
  propose_extension_type:         'Propose choices',
};

const LABELS_DONE: Record<string, string> = {
  search_sf_account:              'Salesforce search complete',
  get_sf_contacts:                'Contacts loaded',
  map_executive:                  'Executives analysed',
  select_executives:              'Recommendations ready',
  init_booking_slots:             'Availability loaded',
  confirm_booking:                'Meeting booked',
  generate_booking_url:           'Booking link ready',
  get_additional_guest_options:   'Contacts loaded',
  get_prep_doc:                   'Report generated',
  generate_call_objective_options:'Call objectives ready',
  confirm_call_objective:         'Call objective saved',
  get_reschedule_link:            'Meeting found',
  confirm_reschedule:             'Reschedule confirmed',
  get_cancel_details:             'Meeting found',
  confirm_cancel:                 'Meeting cancelled',
  get_meeting_for_add_participants:'Meeting found',
  confirm_add_participants:       'Participants added',
  update_session_metadata:        'Session updated',
  dispatch_to_agent:              'Agent ready',
  propose_extension_type:         'Propose choices',
};

const LABELS_ERROR: Record<string, string> = {
  search_sf_account:              'Salesforce search failed',
  get_sf_contacts:                'Failed to load contacts',
  map_executive:                  'Executive analysis failed',
  select_executives:              'Failed to load recommendations',
  init_booking_slots:             'Failed to fetch availability',
  confirm_booking:                'Booking failed',
  generate_booking_url:           'Failed to generate booking link',
  get_additional_guest_options:   'Failed to load contacts',
  get_prep_doc:                   'Report generation failed',
  generate_call_objective_options:'Failed to load objectives',
  confirm_call_objective:         'Failed to save objective',
  get_reschedule_link:            'Meeting not found',
  confirm_reschedule:             'Reschedule failed',
  get_cancel_details:             'Meeting not found',
  confirm_cancel:                 'Cancel failed',
  get_meeting_for_add_participants:'Meeting not found',
  confirm_add_participants:       'Failed to add participants',
};

@Component({
  selector: 'app-tool-activity-pills',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tool-activity-pills.component.html',
  styleUrls: ['./tool-activity-pills.component.scss'],
})
export class ToolActivityPillsComponent {
  @Input() activities: ToolActivity[] = [];

  /** 'row' (default): the original wrapped chip row, used by the pcheck and
   *  ai-role renderers. 'steps': a compact vertical step timeline — one row
   *  per tool call (display name + spinner, flipping to ✓/✗ on its result) —
   *  used by the main Agent Hub chat. Same data, layout-only difference. */
  @Input() layout: 'row' | 'steps' = 'row';

  /** How many rows the 'steps' timeline shows before collapsing the rest
   *  behind a "N more" toggle (an agent like Account Summary can fire a
   *  dozen+ calls, flooding the chat). Row layout is never collapsed. */
  private readonly visibleCount = 4;
  expanded = false;

  get isTruncated(): boolean {
    return this.layout === 'steps' && this.activities.length > this.visibleCount + 1;
  }

  /** Rows to render: everything once expanded (or never truncated), else the
   *  first visibleCount + one extra row shown faded as the visual cutoff. */
  get displayed(): ToolActivity[] {
    return this.expanded || !this.isTruncated
      ? this.activities
      : this.activities.slice(0, this.visibleCount + 1);
  }

  get hiddenCount(): number {
    return this.activities.length - this.visibleCount - 1;
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  getLabel(name: string, status: 'running' | 'done' | 'error'): string {
    const map = status === 'running' ? LABELS_RUNNING
              : status === 'done'    ? LABELS_DONE
              : LABELS_ERROR;
    return map[name] ?? name.replace(/_/g, ' ');
  }
}
